import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Project, listProjects } from '../api/projectsApi';
import { useAuth } from './AuthContext';
import { setActiveProjectId } from '../api/systemsApi';

interface ProjectContextValue {
  currentProject: (Project & { role: string }) | null;
  projects: Project[];
  setCurrentProject: (p: Project & { role: string }) => void;
  refreshProjects: () => Promise<Project[]>;
  syncProjectFromId: (id: string) => void;
  loading: boolean;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

const STORAGE_KEY = 'arch_editor_project_id';

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProjectState] = useState<(Project & { role: string }) | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshProjects = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listProjects();
      setProjects(list);
      const savedId = localStorage.getItem(STORAGE_KEY);
      if (savedId) {
        const found = list.find((p) => p.id === savedId) as (Project & { role: string }) | undefined;
        if (found) setCurrentProjectState(found);
      }
      return list;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep systemsApi in sync with the active project so non-hook code can use it.
  useEffect(() => {
    setActiveProjectId(currentProject?.id ?? null);
  }, [currentProject]);

  // Re-fetch projects whenever auth state changes.
  // Wait for authLoading to resolve before reacting — otherwise the initial
  // isAuthenticated=false fires immediately and clears loading before auth resolves.
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) {
      refreshProjects();
    } else {
      setProjects([]);
      setCurrentProjectState(null);
      localStorage.removeItem(STORAGE_KEY);
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, refreshProjects]);

  const setCurrentProject = (p: Project & { role: string }) => {
    setCurrentProjectState(p);
    localStorage.setItem(STORAGE_KEY, p.id);
    setActiveProjectId(p.id);
  };

  const syncProjectFromId = useCallback((id: string) => {
    setProjects((prev) => {
      const found = prev.find((p) => p.id === id) as (Project & { role: string }) | undefined;
      if (found) {
        setCurrentProjectState(found);
        localStorage.setItem(STORAGE_KEY, id);
      }
      return prev;
    });
  }, []);

  return (
    <ProjectContext.Provider value={{ currentProject, projects, setCurrentProject, syncProjectFromId, refreshProjects, loading }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used inside ProjectProvider');
  return ctx;
}
